import React, { FC, useState, useRef, useCallback, useEffect, ReactElement, ComponentType } from 'react';
import { FormControl, FormControlHandle } from '../Controls/FormControl';
import { CheckboxGroupControl } from '../Controls/CheckboxGroupControl';
import { RadioGroupControl } from '../Controls/RadioGroupControl';
import { InfoTooltip } from '../../InfoTooltip';
import type { Props as InfoTooltipProps } from '../../InfoTooltip/types';
import { UnderscoresToTitleCase } from '../../../utils/StringUtil';
import { GetValidationPattern, GetValidationMessage } from '../../../utils/ValidationUtil';
import { i18n } from '../../../i18n/translate';
import { kebabCase } from 'lodash';
import { formatClassName } from '../../../utils';
import { FormControl as CoreFormControl, Typography, FormLabel as CoreFormLabel } from '../../../core';

/** Common type for any form field value. */
type FieldValue = string | number | boolean | string[] | null | undefined;

export interface FormFieldProps {
  activeLanguage?: string;
  caption?: string;
  children?: ReactElement;
  className?: string;
  data?: { id?: string; name?: string; type?: string };
  dataObjectAction?: string;
  dataObjectAttribute?: string;
  dataObjectId?: string;
  dataObjectType?: string;
  dataValue?: FieldValue;
  disabled?: boolean;
  errorMessage?: string;
  hintLink?: React.ReactNode;
  hintText?: string;
  hideControlLabelAria?: boolean;
  ignorePattern?: boolean;
  inputClassName?: string;
  labelClassName?: string;
  legendHeadingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  isFloatOutside?: boolean;
  languages?: Array<Record<string, string>>;
  multilingual?: boolean;
  name?: string;
  onBlur?: (value: FieldValue) => void;
  onFocus?: () => void;
  onValueChange?: (obj: Record<string, FieldValue>) => void;
  pattern?: string;
  required?: boolean;
  runValidation?: boolean;
  scrollContainerId?: string;
  searchField?: ReactElement;
  srHelpText?: string;
  tooltip?: string;
  tooltipPosition?: InfoTooltipProps['tooltipPosition'];
  validationType?: string;
  valueChangeDelay?: number;
  valueOverride?: boolean;
  wrapperClassName?: string;
  'aria-describedby'?: string;
  type?: string;
}

export const FormField: FC<FormFieldProps> = ({
  activeLanguage,
  caption,
  children,
  className: propClassName,
  data,
  dataObjectAction,
  dataObjectAttribute,
  dataObjectId,
  dataObjectType,
  dataValue,
  disabled,
  errorMessage,
  hintLink,
  hintText,
  hideControlLabelAria,
  ignorePattern,
  inputClassName,
  labelClassName,
  legendHeadingLevel = 'h3',
  isFloatOutside,
  languages,
  multilingual,
  name,
  onBlur,
  onFocus,
  onValueChange,
  pattern,
  required,
  runValidation,
  scrollContainerId,
  searchField,
  srHelpText,
  tooltip,
  tooltipPosition = 'left',
  validationType,
  valueChangeDelay,
  valueOverride,
  wrapperClassName,
  'aria-describedby': ariaDescribedBy,
  ...rest
}) => {
  const [hasError, setHasErrorState] = useState(false);
  const [validationError, setValidationError] = useState<string | false>(false);
  const controlRef = useRef<FormControlHandle | null>(null);

  const setError = useCallback(
    (error: boolean, message?: string) => {
      setHasErrorState(prev => {
        if (prev !== error) {
          if (validationType && error) {
            setValidationError(GetValidationMessage(validationType));
          } else if (error && message) {
            setValidationError(message);
          } else {
            setValidationError(false);
          }
          return error;
        }
        if (!validationType && message !== undefined) {
          setValidationError(message || false);
        }
        return prev;
      });
    },
    [validationType]
  );

  useEffect(() => {
    if (runValidation && controlRef.current?.checkError) {
      controlRef.current.checkError();
    }
  }, [runValidation]);

  // Compute derived values
  const currentLanguage = languages?.filter(lang => Object.keys(lang)[0] === activeLanguage) ?? [];
  const displayCaption =
    multilingual && currentLanguage.length ? `${caption} (${currentLanguage[0][activeLanguage!]})` : caption;

  const effectiveError = errorMessage || hasError;

  const hasHintText = !!hintText;
  const hasEffectiveError = !!effectiveError;
  // Match the IDs used by CoreFormControl for help text and error messages
  const hintDescribedBy = hasHintText ? `${name}-helptext` : undefined;
  const errorDescribedBy = hasEffectiveError ? `${name}-error` : undefined;
  // Chakra's FormErrorMessage automatically links to the input via aria-describedby when
  // ChakraFormControl is isInvalid. We still add explicit ids for non-Chakra child controls
  // (e.g. legacy react-select) that need it spelled out.
  const mergedDescribedBy = [errorDescribedBy, hintDescribedBy, ariaDescribedBy].filter(Boolean).join(' ') || undefined;

  const className = formatClassName([
    propClassName ?? '',
    hasEffectiveError || !!errorMessage ? 'has-error is-invalid' : '',
    required ? 'required-form-control' : '',
    isFloatOutside ? 'fg-floatoutside' : '',
  ]);

  const dataId = dataObjectId || (data?.id ?? undefined);
  const dataType = dataObjectType || (data?.type ?? undefined);
  const dataAttribute = dataObjectAttribute || (data?.name ? data.name : undefined);

  // Detect group controls that require fieldset/legend
  const isGroupControl =
    children &&
    ((children.type as unknown) === CheckboxGroupControl ||
      (children.type as unknown) === RadioGroupControl ||
      (children.type as React.ComponentType)?.displayName === 'CheckboxGroupControl' ||
      (children.type as React.ComponentType)?.displayName === 'RadioGroupControl');

  const resolvedPattern = validationType ? GetValidationPattern(validationType) : pattern;

  const renderedField = children ? (
    React.cloneElement(React.Children.only(children), {
      dataValue,
      setError,
      required,
      name,
      isFloatOutside,
      onValueChange: onValueChange,
      valueChangeDelay,
      ref: (c: FormControlHandle | null) => (controlRef.current = c),
      onBlur,
      onFocus,
      scrollContainerId,
      disabled,
      caption,
      'aria-describedby': mergedDescribedBy,
      'aria-invalid': effectiveError ? 'true' : undefined,
    })
  ) : (
    <FormControl
      {...rest}
      dataValue={dataValue}
      name={name}
      required={required}
      disabled={disabled}
      caption={caption}
      pattern={resolvedPattern ?? pattern}
      ignorePattern={ignorePattern}
      onValueChange={onValueChange}
      onBlur={onBlur}
      onFocus={onFocus}
      valueChangeDelay={valueChangeDelay}
      valueOverride={valueOverride}
      ref={(c: FormControlHandle | null) => (controlRef.current = c)}
      setError={setError}
      className={inputClassName}
      aria-describedby={mergedDescribedBy}
      aria-invalid={effectiveError ? 'true' : undefined}
    />
  );

  // errorMessage: external prop or internal validation error (passed to CoreFormControl)
  const activeError: string | undefined = errorMessage
    ? String(errorMessage)
    : validationError
      ? UnderscoresToTitleCase(String(validationError))
      : undefined;

  // helpText: passed to CoreFormControl; hintLink appended inline
  const activeHelpText = hintText ? (
    <>
      {hintText}
      {hintLink}
    </>
  ) : undefined;

  return (
    <CoreFormControl
      name={name}
      isInvalid={!!(hasError || errorMessage)}
      required={required}
      isDisabled={disabled}
      className={className}
      errorMessage={activeError}
      helpText={activeHelpText}
      data-field-name={name}
      data-object-id={dataId}
      data-object-type={dataType}
      data-object-attribute={dataAttribute || `${kebabCase(name)}-form-field`}
      data-object-action={dataObjectAction}
    >
      {/* fg-field-wrapper carries the flex-item stretch chain for the field slot recipe's column */}
      <div className={formatClassName(['fg-field-wrapper', wrapperClassName])}>
        {isGroupControl ? (
          <fieldset>
            {displayCaption && (
              <CoreFormLabel asLegend id={`${name}-label`}>
                <Typography variant={legendHeadingLevel}>
                  {displayCaption}
                  {required && <span aria-hidden="true">*</span>}
                </Typography>
              </CoreFormLabel>
            )}
            {renderedField}
          </fieldset>
        ) : (
          <>
            {displayCaption && (
              // Core.FormLabel replaces react-bootstrap FormLabel
              <CoreFormLabel
                id={`${name}-label`}
                htmlFor={name}
                className={labelClassName}
                aria-hidden={hideControlLabelAria}
              >
                {searchField || ''} {displayCaption}
                {srHelpText && <span className="sr-only">{srHelpText}</span>}
              </CoreFormLabel>
            )}
            {renderedField}
          </>
        )}
        {tooltip ? (
          <InfoTooltip
            tooltip={tooltip ?? ''}
            tooltipPosition={tooltipPosition}
            label={i18n.t('More information about %s', {
              postProcess: 'sprintf',
              sprintf: [caption],
            })}
          />
        ) : null}
      </div>
    </CoreFormControl>
  );
};

FormField.displayName = 'FormField';
